import styled, { css } from 'styled-components';
import { mq } from '@phishtool/ui';
import { logoBox } from './logo-box';
// Items visible at <768 / >=768 / >=1024. The carousel reads the same tuple for its step maths,
// so the CSS width and the JS offset can never drift apart.
export const visibleCounts = [1, 2, 4];
// Shared with the hook, which schedules the loop's snap-back off the same duration
export const transitionMs = 600;
export const StyledViewport = styled.div.withConfig({ displayName: "StyledViewport", componentId: "sc-91md4z" }) `
  overflow: hidden;
`;
export const StyledTrack = styled.ul.withConfig({ displayName: "StyledTrack", componentId: "sc-yoptxh" }) `
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  list-style: none;
  transform: translateX(-${(props) => props.$offset}%);

  /* Too few items to fill the row reads as broken when left-aligned, but centring an overflowing
     row shifts it off the first item — so compare the count per breakpoint at render time rather
     than waiting on a measurement, which would move the row after the first frame. */
  ${(props) => mq({
    justifyContent: visibleCounts.map((count) => (props.$itemCount > count ? 'flex-start' : 'center')),
})}

  ${(props) => props.$isAnimated &&
    css `
      transition: transform ${transitionMs}ms ease-in-out;
    `}
`;
// Spacing is padding rather than a track gap, so one step stays exactly one item wide
export const StyledItem = styled.li.withConfig({ displayName: "StyledItem", componentId: "sc-1ml4tdl" }) `
  display: flex;
  flex-direction: column;
  align-items: center;

  ${(props) => mq({
    flex: visibleCounts.map((count) => `0 0 ${100 / count}%`),
    gap: [props.theme.spacers.x2, null, props.theme.spacers.x3],
    padding: [`0 ${props.theme.spacers.x3}`, null, `0 ${props.theme.spacers.x6}`],
})}
`;
// The stat is a display size outside the theme's scale, so it's set here rather than via Heading.
// Kept at twice the section heading on each breakpoint (22px -> 44px, 34px -> 68px), so the two
// stay in proportion. -0.02em matches the tracking every typography token uses.
const headingFontSizes = ['44px', null, '68px'];
export const StyledItemHeading = styled.h3.withConfig({ displayName: "StyledItemHeading", componentId: "sc-16r4hkl" }) `
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: ${(props) => props.theme.spacers.x1};
  margin: 0;
  color: ${(props) => props.theme.body.textColor};
  font-weight: ${(props) => props.theme.fontWeights.medium};
  letter-spacing: -0.02em;
  /* Hugs the digits so a note beside them can align to the top of the numeral, not to half-leading */
  line-height: 1;

  ${mq({ fontSize: headingFontSizes })}
`;
const noteLineHeight = 1.333;
// IBM Plex Sans, as fractions of font size: the line box the face reports, its ascent, and how far
// down the tallest ink actually starts for a lining numeral vs. the note's ascenders.
const fontLineBoxRatio = 1.309;
const fontAscentRatio = 1.029;
const digitInkRatio = 0.71;
const noteInkRatio = 0.75;
// Flex aligns boxes, not glyphs — a numeral's ink starts far below its line box top, the note's
// barely does, so top-aligning the boxes leaves the note sitting high. This is that gap: half-leading
// (which moves with line-height) plus the space the face leaves above its tallest ink.
const inkTopRatio = (lineHeight, inkRatio) => (lineHeight - fontLineBoxRatio) / 2 + (fontAscentRatio - inkRatio);
// Qualifies a stat that doesn't read alone, e.g. "8" / "Of the world's largest technology companies"
export const StyledItemHeadingNote = styled.span.withConfig({ displayName: "StyledItemHeadingNote", componentId: "sc-v69dpk" }) `
  max-width: 9em;
  text-align: left;
  font-size: ${(props) => props.theme.typography.x1.fontSize};
  font-weight: ${(props) => props.theme.fontWeights.medium};
  letter-spacing: ${(props) => props.theme.typography.x1.letterSpacing};
  line-height: ${noteLineHeight};

  ${(props) => {
    const noteInkTop = parseFloat(props.theme.typography.x1.fontSize) * inkTopRatio(noteLineHeight, noteInkRatio);
    return mq({
        marginTop: headingFontSizes.map((size) => size === null ? null : `${(parseFloat(size) * inkTopRatio(1, digitInkRatio) - noteInkTop).toFixed(2)}px`),
    });
}}
`;
export const StyledItemDescription = styled.p.withConfig({ displayName: "StyledItemDescription", componentId: "sc-9yl8cj" }) `
  margin: 0;
  text-align: center;
  color: ${(props) => props.theme.colors.oxfordBlue};
  font-size: ${(props) => props.theme.typography.x2.fontSize};
  letter-spacing: ${(props) => props.theme.typography.x2.letterSpacing};
`;
// Every symbol is padded to logoBox by the sprite build, so sizing this box to the box's aspect
// renders all of them at one scale — 1:1 with the design at the desktop height.
const logoHeights = [logoBox.height * 0.78, null, logoBox.height];
export const StyledLogo = styled.svg.withConfig({ displayName: "StyledLogo", componentId: "sc-1ov65sr" }) `
  max-width: 100%;
  color: ${(props) => props.theme.colors.darkEclipse};

  ${mq({
    height: logoHeights.map((height) => (height === null ? null : `${height}px`)),
    width: logoHeights.map((height) => height === null ? null : `${((height * logoBox.width) / logoBox.height).toFixed(2)}px`),
})}
`;
